/**
 * Environment Config - 环境配置管理
 * 统一管理所有环境的配置（API、开发工具等）
 */

// ========================================
// API 基础域名常量
// ========================================

// GO API 基础域名（按环境分组）
const GO_API_BASE = {
  dev: 'https://api.satellai.com/go/dev',
  prod: 'https://api.satellai.com/go/prod'
};

// CMS API 基础域名（按环境分组）
const CMS_API_BASE = {
  dev: 'https://development-kxl9.onrender.com',
  prod: 'https://satellai-web.onrender.com',
  local: 'http://localhost:3001'
};


// ========================================
// 环境配置管理类
// ========================================

class EnvironmentConfig {
  // 静态属性 - API 路径后缀（所有环境共用，避免重复定义）
  static GO_USER_QUERY_PATH = '/api/v1/users/query-by-email';
  static CMS_SUBSCRIPTION_PATH = '/api/v2/web/subscriptions-by-customer';
  static CMS_TRIAL_ELIGIBILITY_PATH = '/api/v2/web/trial-eligibility';
  static CMS_DEVICE_BINDING_PATH = '/api/v2/web/device-binding';
  static CMS_PACKAGES_PATH = '/api/v2/web/packages-by-sncode';
  static CMS_REDEMPTION_MY_CODES_PATH = '/api/v2/web/redemption/my-codes';
  static CMS_REDEMPTION_CHECK_PATH = '/api/v2/web/redemption/check';
  static CMS_REDEMPTION_ADD_MANUAL_PATH = '/api/v2/web/redemption/add-manual';
  static CMS_REDEMPTION_CLAIM_PATH = '/api/v2/web/redemption/claim';
  static CMS_IP_INFO_PATH = '/api/v2/web/ip-info';
  static CMS_FRONTEND_EVENT_LOGS_PATH = '/api/v2/web/frontend-event-logs';
  static CMS_IDENTITY_BIND_PATH = '/api/v2/web/identity/bind';
  static CMS_QUIZ_EVENTS_PATH = '/api/v2/web/quiz-events';
  static CMS_QUIZ_EVENTS_BATCH_PATH = '/api/v2/web/quiz-events/batch';
  static CMS_FAQ_COLLECTIONS_PATH = '/api/v2/web/faq/collections';
  static CMS_FAQ_ARTICLES_PATH = '/api/v2/web/faq/articles';
  static CMS_FAQ_VIEW_PATH = '/api/v2/web/faq/view';
  static CMS_HELP_MENU_PATH = '/api/v2/web/help/menu';
  static CMS_HELP_ARTICLE_PATH = '/api/v2/web/help/article';
  static CMS_HELP_SEARCH_PATH = '/api/v2/web/help/article/search';
  static CLOUDFRONT_APP_LOGIN_PATH = '/sales/auth/app-login';

  constructor() {
    // 默认使用生产环境
    this.environment = 'production';

    // API Key 配置（所有环境共用）
    this.subscriptionApiKey = 'n2Kf0YzP8vXbR5wTjC1sLqGmEoA7HdU3';
  }

  /**
   * 设置当前环境
   * @param {String} env - 环境名称 ('production'、'development' 或 'local')
   */
  setEnvironment(env) {
    if (env === 'production' || env === 'development' || env === 'local') {
      this.environment = env;
      // api-logger 在账户页环境检测前初始化；切换到 local 后同步更新其 CMS 地址。
      if (window.apiLogger) {
        window.apiLogger.apiUrl = this.getLoggerApiUrl();
      }
      console.log(`API 环境已切换为: ${env}`);
    } else {
      console.warn(`未知的环境: ${env}，保持当前环境: ${this.environment}`);
    }
  }

  /**
   * 获取当前环境
   * @returns {String} 当前环境名称
   */
  getEnvironment() {
    return this.environment;
  }

  /**
   * 根据 URL 参数和用户条件自动设置环境
   * 条件：URL 参数包含 environment=dev 或 environment=local，且
   * customer tags 包含 internal 或邮箱后缀是 @satellai.com
   * @param {Array} customerTags - 客户标签数组
   * @param {String} email - 用户邮箱
   * @returns {String} 设置后的环境名称 ('production'、'development' 或 'local')
   */
  checkAndSetEnvironment(customerTags = [], email = '') {
    // local 仅影响 CMS 接口，优先于 dev 主题的默认环境，便于本地联调。
    const urlParams = new URLSearchParams(window.location.search);
    const isLocalRequest = urlParams.get('environment') === 'local';
    const isLocalAuthorizedUser = customerTags.includes('internal') || email.endsWith('@satellai.com');
    if (isLocalRequest && isLocalAuthorizedUser) {
      this.setEnvironment('local');
      console.log('✅ 本地 CMS 环境已启用 (Local CMS Environment)');
      return 'local';
    }

    // 检查 Shopify 模板是否为 dev 分支，是则直接进入测试环境
    const themeName = (window.__SHOPIFY_THEME_NAME__ || '').toLowerCase();
    if (themeName === 'dev' || themeName === 'satellai/dev' || themeName === 'satellai-theme/dev') {
      this.setEnvironment('development');
      console.log('✅ 测试环境已启用 — dev 模板自动切换');
      this.loadVConsole();
      return 'development';
    }

    // 检查 URL 参数
    const hasEnvParam = urlParams.get('environment') === 'dev';
    // 检查 customer tags
    const hasInternalTag = customerTags.includes('internal');

    // 检查邮箱后缀
    const isSatellaiEmail = email.endsWith('@satellai.com');

    // 满足任一条件即可：tags 包含 internal 或 邮箱后缀是 @satellai.com
    const isAuthorizedUser = hasInternalTag || isSatellaiEmail;

    // 同时满足：URL 参数 + 授权用户
    if (hasEnvParam && isAuthorizedUser) {
      this.setEnvironment('development');
      console.log('✅ 测试环境已启用 (Dev Environment)');

      // 自动加载 vConsole 移动端调试工具
      this.loadVConsole();

      return 'development';
    } else {
      this.setEnvironment('production');
      console.log('✅ 生产环境 (Production Environment)');
      return 'production';
    }
  }

  /**
   * 获取当前环境的完整配置（动态生成）
   * @returns {Object} 当前环境的 API 配置对象
   */
  getConfig() {
    // 根据当前环境选择对应的基础域名
    const env = this.environment === 'production' ? 'prod' : 'dev';
    // local 时仅将原开发 CMS 域名替换为本地服务；GO 接口保持不变。
    const cmsApiBase = this.environment === 'local'
      ? CMS_API_BASE.local
      : CMS_API_BASE[env];

    return {
      // GO API - T1 设备用户查询
      t1UserQueryApi: `${GO_API_BASE[env]}${EnvironmentConfig.GO_USER_QUERY_PATH}`,

      // GO API - D1 设备用户查询
      d1UserQueryApi: `${GO_API_BASE[env]}${EnvironmentConfig.GO_USER_QUERY_PATH}`,

      // CMS API - 订阅查询
      subscriptionApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_SUBSCRIPTION_PATH}`,

      // CMS API - 试用权益查询
      trialEligibilityApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_TRIAL_ELIGIBILITY_PATH}`,

      // CMS API - 设备绑定
      deviceBindingApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_DEVICE_BINDING_PATH}`,

      // CMS API - 套餐查询
      packagesBySncodeApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_PACKAGES_PATH}`,

      // CMS API - IP地区信息查询
      ipInfoApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_IP_INFO_PATH}`,
      redemptionMyCodesApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_REDEMPTION_MY_CODES_PATH}`,
      redemptionCheckApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_REDEMPTION_CHECK_PATH}`,
      redemptionAddManualApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_REDEMPTION_ADD_MANUAL_PATH}`,
      redemptionClaimApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_REDEMPTION_CLAIM_PATH}`,
      frontendEventLogsApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_FRONTEND_EVENT_LOGS_PATH}`,
      identityBindApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_IDENTITY_BIND_PATH}`,

      // CMS API - Quiz 埋点事件
      quizEventsApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_QUIZ_EVENTS_PATH}`,

      // CMS API - Quiz 埋点事件（批量）
      quizEventsBatchApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_QUIZ_EVENTS_BATCH_PATH}`,

      // CMS API - FAQ 与帮助中心内容
      faqCollectionsApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_FAQ_COLLECTIONS_PATH}`,
      faqArticlesApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_FAQ_ARTICLES_PATH}`,
      faqViewApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_FAQ_VIEW_PATH}`,
      helpMenuApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_HELP_MENU_PATH}`,
      helpArticleApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_HELP_ARTICLE_PATH}`,
      helpSearchApiUrl: `${cmsApiBase}${EnvironmentConfig.CMS_HELP_SEARCH_PATH}`

    };
  }

  /**
   * 获取指定的 API URL
   * @param {String} key - API 配置键名
   * @returns {String} API URL
   */
  getApiUrl(key) {
    const config = this.getConfig();
    return config[key] || null;
  }

  /**
   * 获取 API Key
   * @returns {String} API Key
   */
  getApiKey() {
    return this.subscriptionApiKey;
  }

  /**
   * 获取前端日志 API URL
   * local 环境使用本地 CMS，其余环境保持生产环境日志地址。
   * @returns {String} 当前环境适用的日志 API URL
   */
  getLoggerApiUrl() {
    const baseUrl = this.environment === 'local' ? CMS_API_BASE.local : CMS_API_BASE.prod;
    return `${baseUrl}${EnvironmentConfig.CMS_FRONTEND_EVENT_LOGS_PATH}`;
  }

  /**
   * 在测试环境下加载 vConsole（移动端调试工具）
   * 仅在 development 环境下加载
   * vConsole 提供移动端友好的调试面板，包括 Console、Network、Element、Storage 等功能
   */
  loadVConsole() {
    if (this.environment === 'development' && !window.vConsole) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/vconsole@latest/dist/vconsole.min.js';
      script.onload = () => {
        window.vConsole = new window.VConsole();
        console.log('📱 vConsole 已加载（移动端调试工具）');
      };
      script.onerror = () => {
        console.warn('⚠️ vConsole 加载失败');
      };
      document.head.appendChild(script);
    }
  }

}

// 创建全局实例，供其他组件使用
window.EnvironmentConfig = new EnvironmentConfig();

// 立即执行 dev 主题自动检测（不依赖 account 页面的调用）
// 仅检测 theme name，customer tags/email 的完整检测仍由 account-user-query.js 负责
(function() {
  var themeName = (window.__SHOPIFY_THEME_NAME__ || '').toLowerCase();
  if (themeName === 'dev' || themeName === 'satellai/dev' || themeName === 'satellai-theme/dev') {
    window.EnvironmentConfig.setEnvironment('development');
    console.log('✅ 测试环境已启用 — dev 模板自动切换');
    window.EnvironmentConfig.loadVConsole();
  }
})();
